// src/lib/lead/locationScope.ts
import type { Lead } from "@/types/lead";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function hasLeadUuidId(lead: Lead): boolean {
  return UUID_RE.test(lead.id);
}

/** Canonical location key for this lead (from payload or DB column fallback). */
export function originLocationKey(lead: Lead, rowLocationId?: string | null): string | null {
  const fromData = (lead.origin_location_id || "").trim();
  if (fromData) return fromData;
  const rid = (rowLocationId || "").trim();
  if (rid) return rid;
  return null;
}

/**
 * Workspace-location visibility (client-side list filter; org RLS still applies):
 * - **Lot selected:** show persisted (UUID) leads stamped for that origin, plus local non-UUID drafts.
 *   Leads with **no** `origin_location_id` are hidden so the list is lot-specific.
 * - **No lot selected** (workspace location id empty / null): show local non-UUID drafts and persisted
 *   UUID leads with **no** origin (org-wide intake: bulk/paste before a lot is chosen). Any org member
 *   in that session sees the same rule; it is not a separate “all locations” role flag—only “has a
 *   working lot selected or not.”
 */
export function leadMatchesWorkspaceLocation(
  lead: Lead,
  workspaceLocationId: string | null | undefined
): boolean {
  const active = (workspaceLocationId || "").trim() || null;

  if (!hasLeadUuidId(lead)) return true;

  const o = originLocationKey(lead);

  if (!active) {
    return !o;
  }

  if (!o) {
    return false;
  }

  return o === active;
}

export function shouldPromptGhlLocationMismatch(
  lead: Lead,
  workspaceLocation: { id?: string | null } | null | undefined
): boolean {
  if (lead.isTest) return false;
  const o = (lead.origin_location_id || "").trim();
  const cur = (workspaceLocation?.id || "").trim();
  if (!o || !cur) return false;
  return o !== cur;
}

export function stampLeadOriginIfBlank(
  lead: Lead,
  locationId: string | null | undefined,
  locationName: string | null | undefined
): Lead {
  const lid = (locationId || "").trim();
  if (!lid) return lead;
  if ((lead.origin_location_id || "").trim()) return lead;
  return {
    ...lead,
    origin_location_id: lid,
    origin_location_name: locationName ?? lead.origin_location_name ?? null,
  };
}
